// src/modules/goldVault/pages/InventoryAging.jsx
import React, { useEffect, useState } from 'react';
import {
  Card, Row, Col, Statistic, Table, Typography, Spin, Button, Tabs,
  Select, Tag,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getBranchAging, getHOAging } from '../api';
import { fetchBranches } from '../../branches/api';

const { Title } = Typography;
const fmtW = (v) => `${Number(v || 0).toFixed(3)}g`;

const caseColumns = [
  { title: 'Case No.', dataIndex: 'caseId', key: 'caseId', width: 110 },
  { title: 'Customer', key: 'customer', width: 130, render: (_, r) => r.customerId?.name || '—' },
  { title: 'Phone', key: 'phone', width: 110, render: (_, r) => r.customerId?.phone || '—' },
  { title: 'Gross Wt', dataIndex: 'totalGrossWeight', key: 'gw', width: 90, render: fmtW },
  { title: 'Net Wt', dataIndex: 'totalNetWeight', key: 'nw', width: 90, render: fmtW },
  { title: 'Age', key: 'age', width: 80,
    render: (_, r) => {
      const days = Math.floor((Date.now() - new Date(r.createdAt)) / (1000 * 60 * 60 * 24));
      return `${days}d`;
    } },
  { title: 'Since', dataIndex: 'createdAt', key: 'ca', width: 110,
    render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '—' },
];

function AgingBuckets({ buckets }) {
  return (
    <>
      <Row gutter={[16, 8]} style={{ marginBottom: 16 }}>
        {buckets.map((b) => (
          <Col xs={12} sm={6} key={b.label}>
            <Card size="small" bordered>
              <Statistic title={b.label} value={`${b.count} cases`} />
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                {fmtW(b.totalNetWeight)} net
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      {buckets.map((b) => b.cases.length > 0 && (
        <Card key={b.label} title={`${b.label} — ${b.count} cases`} size="small" style={{ marginBottom: 12 }}>
          <Table
            dataSource={b.cases} rowKey="_id" columns={caseColumns}
            size="small" scroll={{ x: 700 }} pagination={false}
          />
        </Card>
      ))}
    </>
  );
}

function BranchAgingTab() {
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBranches().then((r) => {
      const list = r.data || [];
      setBranches(list);
      if (list.length) setSelectedBranch(list[0]._id);
    }).catch(() => {});
  }, []);

  useEffect(() => { if (selectedBranch) load(); }, [selectedBranch]);

  const load = async () => {
    if (!selectedBranch) return;
    setLoading(true);
    try {
      const res = await getBranchAging(selectedBranch);
      setBuckets(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col>
          <Select placeholder="Select Branch" style={{ width: 200 }} value={selectedBranch} onChange={setSelectedBranch}>
            {branches.map((b) => <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>)}
          </Select>
        </Col>
        <Col><Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button></Col>
      </Row>
      <Spin spinning={loading}>
        <AgingBuckets buckets={buckets} />
      </Spin>
    </div>
  );
}

function HOAgingTab() {
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getHOAging();
      setBuckets(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <Row style={{ marginBottom: 12 }}>
        <Col><Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button></Col>
      </Row>
      <Spin spinning={loading}>
        <AgingBuckets buckets={buckets} />
      </Spin>
    </div>
  );
}

export default function InventoryAging() {
  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 20 }}>Inventory Aging</Title>
      <Tabs
        items={[
          { key: 'branch', label: 'Branch Vault Aging', children: <BranchAgingTab /> },
          { key: 'ho', label: 'HO Vault Aging', children: <HOAgingTab /> },
        ]}
      />
    </div>
  );
}
